import { useCallback, useRef, useState } from 'react';
import { useInitEmblaCarousel } from '@/components/EmblaCarousel/hooks/useInitEmblaCarousel';
import { useResizeObserver } from '@/hooks';

const COL_MIN_WIDTH = 160;

export const useComparisonSlider = (totalSlides: number) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);

  const { emblaRef, emblaApi, selectedIndex } = useInitEmblaCarousel({
    options: { loop: false, align: 'start', containScroll: 'trimSnaps' },
  });

  const handleResize = useCallback(
    (entry: ResizeObserverEntry) => {
      const count = Math.max(
        1,
        Math.min(
          totalSlides,
          Math.floor(entry.contentRect.width / COL_MIN_WIDTH)
        )
      );
      setSlidesPerView(count);
    },
    [totalSlides]
  );

  useResizeObserver(containerRef, handleResize);

  const handleCardResize = useCallback((entry: ResizeObserverEntry) => {
    setCardHeight(
      entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
    );
  }, []);

  useResizeObserver(cardRef, handleCardResize);

  const canPrev = selectedIndex > 0;
  const canNext = selectedIndex < totalSlides - slidesPerView;

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return {
    emblaRef,
    containerRef,
    cardRef,
    cardHeight,
    selectedIndex,
    slidesPerView,
    canPrev,
    canNext,
    scrollPrev,
    scrollNext,
  };
};
